/**
 * SpecialEditorHost — CC-3 (fullstack §3 CC-3 / §5.1 dialog-spine foundation).
 *
 * The SHARED dialog host for every entity kind whose property capability set
 * declares `requiresSpecialEditor: true` (`getEntityPropertyCapabilities` —
 * Block / Hatch / Rectangular Associative Array / Spline / Wipeout / Dimension /
 * Multileader / Revision Cloud / Viewport). AutoCAD surfaces these behind
 * dedicated modal editors ("Hatch and Gradient", Revision-cloud properties,
 * the associative-array Properties block) rather than the plain Properties
 * grid, because their edits trigger a REGEN of derived geometry (recomputed
 * arcs, re-tiled array copies, re-filled hatch) instead of a flat field patch.
 *
 * SCOPE (CC-3): this file is the host + the "write-back through a command-engine
 * op → regen" plumbing ONLY. The per-entity FORMS are separate downstream
 * slices (REVCLOUD-PROPS-EDITOR / HATCH-EDIT-DIALOG / ARRAY-SPEC-EDITOR); each
 * registers its `{ titleKey, form }` in `AppSpecialEditorDialogs`. The host
 * gives those forms a stable CONTRACT:
 *   - the typed `DrawingEntity` being edited (NOT the string-bag EntityPreview),
 *   - its DEFUSE S15 typed `TypedPropertyDescriptor[]` (the read model — forms
 *     resolve values via `descriptor.typedFieldRef.read(entity)`, per ПРИОРИТЕТ
 *     #1: new CAD semantics live in the package-level typed model, the UI
 *     projects FROM it), and
 *   - an `onDraftChange` callback to lift the form's dirty-state + the
 *     command-engine write-back ops up to the host.
 *
 * The host owns the chrome (title / OK / Cancel / Apply — RU+EN via `useT` /
 * `messages.ts`) and the write-back → regen cycle (`useSpecialEditorRegenCommit`).
 * It calls NO per-entity logic itself — that stays in the injected form.
 *
 * ## Function dependency map
 *
 * SpecialEditorHost (component)
 *   props: { titleKey?, entity, form, onClose, regenSummaryKey?, width? }
 *   useT()                         -> i18n.useT
 *   useSpecialEditorRegenCommit()  -> dispatch + recordViewOp (this file)
 *   getTypedPropertyDescriptors()  -> @dxf-ai-editor/command-engine (DEFUSE S15)
 */

import { useCallback, useMemo, useState } from "react";
import type { ComponentType, ReactElement } from "react";

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";
import {
  getTypedPropertyDescriptors,
  type TypedPropertyDescriptor,
} from "@dxf-ai-editor/command-engine";

import { useT } from "../i18n";
import type { MessageKey } from "../i18n";
import { useEditorStore } from "../state/editorStore";

/* ───────────────────────── Per-entity form contract ─────────────────────────
 * The per-entity FORMS are OUT OF SCOPE of CC-3. This is the contract they
 * implement so the shared host can drive them. A form is a pure controlled
 * component: it derives its draft from `entity` + `descriptors`, and on every
 * draft change reports `{ dirty, buildWriteBack }` up via `onDraftChange`. It
 * never dispatches — the host owns the write-back → regen cycle.
 * ────────────────────────────────────────────────────────────────────────── */

/** The form's lifted draft state — what the host needs to drive Apply / OK. */
export interface SpecialEditorFormDraft {
  /** true while the form's draft differs from the persisted entity. Gates the
   *  Apply button (OK stays enabled — a no-op Apply then close). */
  dirty: boolean;
  /**
   * Build the command-engine write-back op(s) for the CURRENT draft — the ops
   * the host dispatches on Apply / OK. Returning `null` or `[]` ABORTS the
   * apply (e.g. the draft failed the form's own validation): the host
   * dispatches nothing and stays open. These are ordinary `EditorCommand`s
   * (`properties/apply`, `geometry/edit-hatch-boundary`, …) — the same safe
   * contour every other edit flows through; the host adds only the regen
   * signal on top.
   */
  buildWriteBack: () => readonly EditorCommand[] | null;
}

/** Props the host passes DOWN to an injected per-entity form. */
export interface SpecialEditorFormProps {
  /** the typed entity being edited (canonical `DrawingEntity`, re-resolved from
   *  the live document after each write-back so the form re-derives its
   *  baseline — NOT the string-bag EntityPreview). */
  entity: DrawingEntity;
  /** the DEFUSE S15 typed descriptors for this entity kind (the read model). */
  descriptors: readonly TypedPropertyDescriptor[];
  /** lift the current draft (dirty + write-back builder) up to the host. */
  onDraftChange: (draft: SpecialEditorFormDraft) => void;
}

/** A per-entity special-editor form (registered per kind in the downstream
 *  REVCLOUD-PROPS-EDITOR / HATCH-EDIT-DIALOG / ARRAY-SPEC-EDITOR slices). */
export type SpecialEditorFormComponent = ComponentType<SpecialEditorFormProps>;

/** The pristine lifted draft: nothing dirty, `buildWriteBack` a no-op abort
 *  (`() => null`). The host starts here and returns here on every entity swap,
 *  so a freshly-opened entity can never inherit the previous form's write-back. */
const INITIAL_DRAFT: SpecialEditorFormDraft = {
  dirty: false,
  buildWriteBack: () => null,
};

/* ─────────────────── write-back → regen plumbing (the CC-3 core) ─────────── */

/** Dispatch the write-back op(s), then fire the regen signal. Returns `false`
 *  (a no-op) when there is nothing to write. `batchLabel` is the user-facing
 *  Undo-tooltip label used when a >1-command write-back is wrapped in a
 *  grouped-undo batch (the host passes the dialog title). */
export type SpecialEditorCommit = (
  commands: readonly EditorCommand[] | null,
  regenSummary: string,
  batchLabel: string,
) => boolean;

/**
 * The one write-back → regen cycle every special editor shares.
 *
 *   1. WRITE-BACK — dispatch the command-engine op(s) through the store's
 *      `dispatch` (the safe contour: validation → geometry reducer → audit →
 *      undo). The op's reducer performs the actual geometry REGEN — recomputing
 *      the derived geometry from the patched typed fields (revcloud arcs from
 *      the new chord length, the hatch fill from the new boundary, the array
 *      copies from the new counts) — and the reactive renderer redraws.
 *
 *      A `buildWriteBack` MAY return MORE THAN ONE command (the contract
 *      allows it). Dispatched raw, each mutating op is a SEPARATE undo entry,
 *      so one Apply/OK would need several Undo presses and a single Undo could
 *      leave the edit half-applied. So a multi-command write-back is wrapped in
 *      a `history/begin-batch` … `history/commit-batch` envelope (the same
 *      grouped-undo mechanism grip-drag and the AI turn use): the store
 *      aggregates the ops into ONE `HistoryEntry`, and a single Ctrl-Z reverts
 *      the whole commit. A single command is already one undo entry, so it
 *      dispatches raw — no empty-batch churn.
 *
 *      REFUSE-TO-NEST: the host only opens (and owns the lifecycle of) a batch
 *      it started. `history/begin-batch` NESTS by incrementing `current.depth`
 *      when a batch is already open, and `history/abort-batch` unwinds the
 *      ENTIRE `current` batch regardless of depth — so opening our own batch
 *      while an OUTER batch is in progress (a command-line / agent-bridge / grip
 *      turn) and then aborting on a mid-write throw would roll back the outer
 *      batch's unrelated earlier work. So when a batch is already open we do NOT
 *      open our own: the commands dispatch raw and accumulate into the outer
 *      batch (still one undo — the outer owner groups + owns commit/abort), and
 *      a throw propagates WITHOUT us aborting the batch we do not own. We only
 *      open + abort-on-throw when there is NO outer batch, where the batch is
 *      exclusively ours and aborting can touch nothing but our own commands.
 *   2. REGEN SIGNAL — record a `regen` view-op, matching AutoCAD's implicit
 *      REGEN after a special-editor Apply (the same `recordViewOp` mechanism
 *      REGEN-COMMAND uses). This is the audited display-refresh signal and the
 *      seam REGEN-DISPLAY-REFRESH hooks renderer-cache recompute onto.
 */
export function useSpecialEditorRegenCommit(): SpecialEditorCommit {
  // Read the store ACTIONS non-reactively at call time (they are stable across
  // the store's life — `viewCommands.ts` reads `recordViewOp` the same way).
  // The host must NOT reactively subscribe to `dispatch`/`recordViewOp`: that
  // would re-render the dialog on every unrelated store mutation.
  return useCallback<SpecialEditorCommit>(
    (commands, regenSummary, batchLabel) => {
      if (!commands || commands.length === 0) return false;
      const { dispatch, recordViewOp } = useEditorStore.getState();
      // Own a batch ONLY for a multi-command write-back with NO outer batch
      // already open (refuse-to-nest — see the doc comment). Otherwise dispatch
      // raw: a single command is already one undo entry, and a nested
      // multi-command write-back accumulates into the outer owner's batch.
      const outerBatchOpen =
        useEditorStore.getState().state.history.current !== null;
      const ownBatch = commands.length > 1 && !outerBatchOpen;
      if (ownBatch) {
        // Our own batch → ONE undo entry, one Ctrl-Z reverts the whole commit.
        // Abort-on-throw rolls back the partial write; because the batch is
        // exclusively ours (no outer batch open), the abort touches nothing else.
        dispatch({
          type: "history/begin-batch",
          source: "toolbar",
          label: batchLabel,
        });
        try {
          for (const command of commands) dispatch(command);
        } catch (error) {
          dispatch({ type: "history/abort-batch", source: "toolbar" });
          throw error;
        }
        dispatch({ type: "history/commit-batch", source: "toolbar" });
      } else {
        // Single command, OR nested inside an outer batch we must not manage.
        // Dispatch raw; a throw propagates to the OUTER owner without us aborting
        // (their) batch — they decide commit vs abort for the whole turn.
        for (const command of commands) dispatch(command);
      }
      recordViewOp({ opType: "regen", summary: regenSummary, source: "toolbar" });
      return true;
    },
    [],
  );
}

/* ──────────────────────────────── Host ──────────────────────────────────── */

export interface SpecialEditorHostProps {
  /** i18n key for the dialog title — each editor supplies its own ("Hatch and
   *  Gradient" / Revision-cloud / Array). Defaults to the generic title. */
  titleKey?: MessageKey;
  /** the typed entity being edited (kind must be `requiresSpecialEditor`). */
  entity: DrawingEntity;
  /** the injected per-entity form (downstream slice). */
  form: SpecialEditorFormComponent;
  /** close the dialog (host calls after OK and Cancel). */
  onClose: () => void;
  /** i18n key for the regen telemetry summary; defaults to the generic one. */
  regenSummaryKey?: MessageKey;
  /** dialog width in px (default 520 — matches the dialog suite). */
  width?: number;
}

export function SpecialEditorHost({
  titleKey = "special_editor.title",
  entity,
  form: Form,
  onClose,
  regenSummaryKey = "special_editor.regen_summary",
  width = 520,
}: SpecialEditorHostProps): ReactElement {
  const t = useT();
  const commit = useSpecialEditorRegenCommit();

  // DEFUSE S15 typed read model for the form — resolved from the entity kind.
  const descriptors = useMemo(
    () => getTypedPropertyDescriptors(entity.type),
    [entity.type],
  );

  // The form lifts its draft here. `buildWriteBack: () => null` until the form
  // reports (dirty=false ⇒ Apply disabled), so the host is safe before mount.
  const [draft, setDraft] = useState<SpecialEditorFormDraft>(INITIAL_DRAFT);

  // RESET the lifted draft whenever the edited entity CHANGES (a different id).
  // A single host instance can outlive one entity: the controller may swap the
  // open special entity while the host is mounted, or an Apply re-resolves a
  // fresh identity. Without this, a stale `buildWriteBack` captured from the
  // PREVIOUS entity's form would survive into the new one — Apply/OK could stay
  // enabled and re-dispatch the OLD entity's edits from the NEW dialog. React's
  // "reset state when a prop changes" pattern: adjust during render (not an
  // effect) so there is no interaction frame where the stale draft is live.
  const [draftEntityId, setDraftEntityId] = useState(entity.id);
  if (entity.id !== draftEntityId) {
    setDraftEntityId(entity.id);
    setDraft(INITIAL_DRAFT);
  }

  const apply = useCallback((): boolean => {
    if (!draft.dirty) return false;
    const committed = commit(
      draft.buildWriteBack(),
      t(regenSummaryKey),
      t(titleKey),
    );
    if (committed) {
      // Apply is SPENT by a successful commit: reset the lifted draft so Apply
      // disables until the form reports a NEW dirty draft. The write-back
      // updates the entity IN PLACE (same id is the common case), so the
      // render-phase id-change reset above never fires for it — without this the
      // host would keep the just-applied `buildWriteBack` live, and a second
      // Apply click would re-dispatch the SAME (now stale) ops + record another
      // spurious regen. The host OWNS this Apply-spent semantics (AutoCAD
      // parity); it must NOT depend on every injected form separately
      // re-reporting a clean baseline after the same-id commit. A commit that
      // ABORTED (`buildWriteBack` → null/[], `committed === false`) keeps the
      // dirty draft so the user can still fix the invalid input.
      setDraft(INITIAL_DRAFT);
    }
    return committed;
  }, [draft, commit, t, regenSummaryKey, titleKey]);

  const handleApply = useCallback(() => {
    apply();
  }, [apply]);

  // OK = Apply-then-close, but the close is GATED on the apply outcome so it
  // honours the `buildWriteBack` abort contract (SpecialEditorFormDraft): a
  // dirty draft whose `buildWriteBack()` returns `null`/`[]` failed the form's
  // own validation — `apply()` dispatches nothing and returns `false`, and OK
  // must then STAY OPEN rather than silently discard the invalid edit (AutoCAD
  // parity — a special editor rejects OK on invalid input, it does not close).
  // A clean form (`!draft.dirty`) has nothing to apply, so OK just closes.
  // (`apply()` also returns `false` on a clean form, hence the explicit dirty
  // guard — the close must not depend on the no-op clean-apply return.)
  const handleOk = useCallback(() => {
    if (!draft.dirty || apply()) onClose();
  }, [draft.dirty, apply, onClose]);

  // Cancel is a clean discard: nothing was committed, so closing drops the
  // draft — the form re-derives its baseline from `entity` on the next open.
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <div
      className="dimstyle-editor-overlay"
      data-testid="special-editor-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        className="dimstyle-editor"
        data-testid="special-editor"
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="special-editor-title"
      >
        <div className="dimstyle-editor-title" id="special-editor-title">
          {t(titleKey)}
        </div>
        <div data-testid="special-editor-body">
          <Form
            entity={entity}
            descriptors={descriptors}
            onDraftChange={setDraft}
          />
        </div>
        <div className="dimstyle-editor-actions">
          <button
            type="button"
            className="dimstyle-editor-btn"
            onClick={handleCancel}
            data-testid="special-editor-cancel"
          >
            {t("special_editor.cancel")}
          </button>
          <button
            type="button"
            className="dimstyle-editor-btn"
            onClick={handleApply}
            disabled={!draft.dirty}
            data-testid="special-editor-apply"
          >
            {t("special_editor.apply")}
          </button>
          <button
            type="button"
            className="dimstyle-editor-btn primary"
            onClick={handleOk}
            data-testid="special-editor-ok"
            autoFocus
          >
            {t("special_editor.ok")}
          </button>
        </div>
      </div>
    </div>
  );
}
